import React, { memo, useState } from "react";
import { defineMessages } from "react-intl";
import { FormControlLabel, Switch, Button } from "@mui/material";
import { GridPanelWrapper, GridPanelHeader, GridPanelContent, GridPanelFooter } from "@mui/x-data-grid";
import { makeStyles } from "tss-react/mui";
import { LocalizedText } from "../../i18n/LocalizedText.js";
import { Icon } from "../../icons/Icon.js";
import { CustomIcon } from "../../icons/types.js";
import "../../icons/ExpandMoreIcon.js";
import "../../input/validation.js";
import "../../input/DatePicker.js";
import "../../input/DateRangePicker.js";
import "../../input/StyledInput.js";
import { FormTextInputCleanable } from "../../input/FormInput.js";
import "../../input/StyledFormInputHoc.js";
import "moment-timezone";
import "../../input/DateSinglePicker.localize.js";
import "../../input/hooks.js";
const localized = defineMessages({
  searchInputPlaceholder: {
    id: "GridColumnPanel_searchInputPlaceholder",
    defaultMessage: "Find column"
  },
  hideAllButtonLabel: {
    id: "GridColumnPanel_hideAllButtonLabel",
    defaultMessage: "Hide all"
  },
  showAllButtonLabel: {
    id: "GridColumnPanel_showAllButtonLabel",
    defaultMessage: "Show all"
  }
});
const useStyles = makeStyles()((theme) => ({
  gridColumnsPanelWrapper: {
    display: "flex",
    flexDirection: "column",
    height: "max-content",
    maxHeight: "550px",
    backgroundColor: "#ffffff",
    borderRadius: theme.custom.defaultBorderRadius * 2
  },
  gridColumnsPanelHeader: {
    padding: theme.spacing(1.5),
    "& .MuiFormControl-root": {
      margin: 0
    }
  },
  gridColumnsPanelSearchInputIcon: {
    fontSize: 12,
    color: theme.palette.secondary.main
  },
  gridColumnsPanelContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    overflow: "auto",
    maxHeight: "unset",
    padding: `0 ${theme.spacing(1.5)} ${theme.spacing()}`
  },
  gridColumnsPanelColumn: {
    marginLeft: theme.spacing(1.5),
    "& .MuiSwitch-root": {
      marginRight: theme.spacing()
    }
  },
  gridColumnsPanelFooter: {
    padding: `${theme.spacing(1.75)} ${theme.spacing(1.5)}`,
    borderTop: `1px solid ${theme.palette.secondary.light}`,
    "& .MuiButton-text": {
      padding: 0,
      minWidth: "unset"
    }
  }
}));
function GridColumnsPanelInner({ pinnedColumnNames, onColumnsVisibilityUpdate, allColumns }) {
  const { classes } = useStyles();
  const [searchValue, setSearchValue] = useState("");
  const columns = allColumns?.filter((c) => !pinnedColumnNames?.includes(c.field));
  const searchedColumns = React.useMemo(() => {
    let result = columns;
    if (searchValue) {
      result = columns.filter((column) => column?.headerName?.toLowerCase()?.includes(searchValue.toLowerCase()));
    }
    return result;
  }, [allColumns, searchValue]);
  function handleSearchValueChange(event) {
    setSearchValue(event.target.value);
  }
  function handleColumnClick(event) {
    const name = event.target.name;
    const column = allColumns.find((c) => c.field === name);
    const newColumns = column.hide ? showColumns([name]) : hideColumns([name]);
    onColumnsVisibilityUpdate(newColumns);
  }
  function handleToggleAllColumns(value) {
    const searchedColumnNames = searchedColumns?.map((c) => c.field);
    const newColumns = value ? hideColumns(searchedColumnNames) : showColumns([...searchedColumnNames]);
    onColumnsVisibilityUpdate(newColumns);
  }
  function hideColumns(names) {
    return allColumns.filter((c) => !c.hide && !names.includes(c.field)).map((c) => c.field);
  }
  function showColumns(names) {
    return [.../* @__PURE__ */ new Set([...allColumns.filter((c) => !c.hide).map((c) => c.field), ...names])];
  }
  function handleClearSearch() {
    setSearchValue("");
  }
  return /* @__PURE__ */ React.createElement(GridPanelWrapper, { className: classes.gridColumnsPanelWrapper }, /* @__PURE__ */ React.createElement(GridPanelHeader, { className: classes.gridColumnsPanelHeader }, /* @__PURE__ */ React.createElement(
    FormTextInputCleanable,
    {
      startAdornment: /* @__PURE__ */ React.createElement(Icon, { icon: CustomIcon.Search, className: classes.gridColumnsPanelSearchInputIcon }),
      placeholder: localized.searchInputPlaceholder,
      value: searchValue,
      onChange: handleSearchValueChange,
      clear: handleClearSearch
    }
  )), /* @__PURE__ */ React.createElement(GridPanelContent, { className: classes.gridColumnsPanelContent }, searchedColumns.map((column) => /* @__PURE__ */ React.createElement("div", { key: column.field, className: classes.gridColumnsPanelColumn }, /* @__PURE__ */ React.createElement(
    FormControlLabel,
    {
      control: /* @__PURE__ */ React.createElement(Switch, { checked: !column.hide, onClick: handleColumnClick, name: column.field, color: "secondary" }),
      label: column.headerName || column.field
    }
  )))), /* @__PURE__ */ React.createElement(GridPanelFooter, { className: classes.gridColumnsPanelFooter }, /* @__PURE__ */ React.createElement(Button, { onClick: () => handleToggleAllColumns(true), color: "primary" }, /* @__PURE__ */ React.createElement(LocalizedText, { label: localized.hideAllButtonLabel })), /* @__PURE__ */ React.createElement(Button, { onClick: () => handleToggleAllColumns(false), color: "primary" }, /* @__PURE__ */ React.createElement(LocalizedText, { label: localized.showAllButtonLabel }))));
}
const GridColumnsPanel = memo(GridColumnsPanelInner);
export {
  GridColumnsPanel
};
//# sourceMappingURL=GridColumnsPanel.js.map
